/**
 * A12 — openplzapi.org helpers for DE postal-code ↔ city autofill.
 *
 * Both functions are BEST-EFFORT:
 *   • return an empty array on network / API errors
 *   • never throw — callers can `await` without a try/catch
 *   • silently no-op if the input is empty
 *
 * The endpoint returns arrays of `Locality` objects; we normalise them
 * to plain strings so the caller doesn't have to know the API shape.
 *
 * Docs: https://openplzapi.org
 */
const BASE = "https://openplzapi.org/de/Localities";

async function _get(url) {
  try {
    const res = await fetch(url, { headers: { accept: "application/json" } });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("plzLookup:", err);
    return [];
  }
}

/** PLZ → distinct city names. Empty array = no match / network error. */
export async function lookupCityByPlz(plz) {
  const clean = String(plz || "").trim();
  if (!/^\d{4,5}$/.test(clean)) return [];
  const data = await _get(`${BASE}?postalCode=${encodeURIComponent(clean)}`);
  const names = new Set();
  for (const row of data) {
    if (row && typeof row.name === "string") names.add(row.name);
  }
  return [...names];
}

/** City → distinct PLZ codes. Empty array = no match / network error. */
export async function lookupPlzByCity(city) {
  const clean = String(city || "").trim();
  if (clean.length < 2) return [];
  const data = await _get(`${BASE}?name=${encodeURIComponent(clean)}`);
  const plz = new Set();
  for (const row of data) {
    if (row && typeof row.postalCode === "string") plz.add(row.postalCode);
  }
  return [...plz].sort();
}
